import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import {
  BakeasyChip,
  BakeasyChipDuplicate,
} from '../../components/common/BakeasyChip';
import * as Button from '../../components/buttons/BakeasyButtons';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { useNavigate } from 'react-router-dom';
import {
  downloadOrders,
  fetchOrderHistory,
  OrderHistoryState,
} from '../../redux_store/slices/OrderHistorySlice';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { ORDER_LEGENDS_COLORS, UNAUTHORIZED } from '../../constants/Constants';
import { authActions } from '../../redux_store/slices/authSlice';
import { formatDateTime } from '../../utils/DateUtils';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { t } from 'i18next';
import { orderHistorytableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import BakeasyLegendRow from '../../components/common/BakeasyLegendRow';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const OrderHistory: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();

  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);

  const isMobile = useMediaQuery('(max-width:600px)');
  const [orderList, setOrderList] = useState<any[]>([]);
  const [ingredientList, setIngredientList] = useState<any[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<string | null>(null);
  const [generateClicked, setGenerateClicked] = useState<boolean>(false);
  const [productQuantity, setProductQuantity] = useState<any>('');
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } =
    useAppSelector(OrderHistoryState);

  const refreshPageData = async (
    newPage: number,
    search?: string,
    filter?: {} | undefined
  ) => {
    let payload = await preparePayload(
      {
        limit: rowsPerPage,
        page: newPage + 1,
        sortBy: 'orderDate',
        sortDirection: -1,
      },
      filter,
      search,
      newPage
    );
    await dispatch(fetchOrderHistory(payload));
    // await fetchData(fetchOrderHistory, payload);
  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((orders: any) => {
        return {
          _id: orders?._id,
          orderId: orders?.orderId,
          onOrderIdClick: () => {
            // navigate('/orderhistoryDetails', {
            //   state: { isView: true, _id: orders?._id, orderId: orders?.orderId }
            // });

            if (orders?.status === 'Complete') {
              navigate('/invoiceDetails', { state: { id: orders?._id } });
            } else if (orders?.status === 'Shipped') {
              navigate('/shippingdetails', { state: { id: orders?._id } });
            } else {
              navigate('/orderhistoryDetails', {
                state: {
                  isView: true,
                  _id: orders?._id,
                  orderId: orders?.orderId,
                },
              });
            }
          },
          highPriority: orders?.priority,
          ponumber: orders?.poNumber,
          orderDate: orders?.orderDate ? formatDateTime(orders?.orderDate) : '',
          expecteddate: orders?.requestedDeliveryDate,
          // ? formatDateTime(orders?.requestedDeliveryDate)
          // : '',
          invoiceddate: orders?.invoiceDate
            ? formatDateTime(orders?.invoiceDate)
            : '',
          ordervalue:
            orders?.totalAmount || orders?.totalAmount === 0
              ? parseFloat(orders?.totalAmount)?.toFixed(2)
              : parseFloat('0')?.toFixed(2),
          status: <BakeasyChip label={orders?.status} />,
          duplicate: (
            <BakeasyChipDuplicate
              onClick={() => {
                navigate('/createOrder', {
                  state: {
                    from: 'DuplicateClick',
                    _id: orders?._id,
                    orderId: orders?.orderId,
                  },
                });
              }}
            />
          ),
        };
      });

      setOrderList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function openFilterOrderHistoryConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'orderHistoryfilter',
      open: true,
      title: t('ORDER_HISTORY_FILTER'),
      data: filter,
      onNoClick: (event: any) => {},
      onYesClick: async (event: any, param: any) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      },
    });
  }

  function orderHistoryUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={'ORDER_HISTORY'} />

          <Grid
            container
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (
                    event?.target?.value?.length === 0 ||
                    event?.target?.value?.length > 2
                  ) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0
                        ? undefined
                        : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(
                  event: React.KeyboardEvent<HTMLInputElement>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div
                className="tableOptions"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <Button.Print
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadOrders({ ...filter, downloadType: 'pdf' })
                    );
                  }}
                />
                <Button.Export
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadOrders({ ...filter, downloadType: 'csv' })
                    );
                  }}
                />
                <Button.Filter
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    openFilterOrderHistoryConfirmDialog();
                  }}
                />
                {/* <BakeasyButton
									fullWidth={false}
									className="tableOption"
									label={t("CREATE_ORDER")}
									onClick={function (
										event: React.MouseEvent<HTMLButtonElement, MouseEvent>
									): void {
										//openFilterShippingConfirmDialog();
									}}
								/> */}
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />
          <BakeasyLegendRow colorLabels={ORDER_LEGENDS_COLORS} />

          <Grid>
            {orderList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={orderHistorytableHeaders}
                tableData={orderList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return orderHistoryUI();
};
